import { useState, useEffect, useMemo } from 'react';
import { STATUS_OK, STATUS_ERROR, STATUS_SLOW, STATUS_INFLIGHT } from './ApiTracker';

/**
 * ApiDebugPanel — Renders the comprehensive API debugging overlay.
 *
 * Shows:
 *   - Summary stats bar (total calls, errors, slow, inflight)
 *   - Scrollable log of all API calls with timing breakdown
 *   - Server-side timing details when available
 *   - Color-coded status indicators
 *   - Expandable entries for full detail
 */
function ApiDebugPanel({ tracker, wsLogs = [], analyticsDebug = {}, pendingAnalytics = [], moveStats = null }) {
  const [entries, setEntries] = useState([]);
  const [stats, setStats] = useState({ total: 0, errors: 0, slow: 0, inflight: 0 });
  const [expanded, setExpanded] = useState(new Set());
  const [filter, setFilter] = useState('all'); // all | errors | slow

  // Subscribe to tracker changes
  useEffect(() => {
    if (!tracker) return;
    const update = () => {
      setEntries(tracker.snapshot());
      setStats(tracker.stats());
    };
    tracker.onChange = update;
    update();
    return () => { tracker.onChange = () => {}; };
  }, [tracker]);

  const filteredEntries = useMemo(() => {
    if (filter === 'errors') return entries.filter(e => e.status === STATUS_ERROR);
    if (filter === 'slow') return entries.filter(e => e.slow);
    return entries;
  }, [entries, filter]);

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const statusIcon = (status) => {
    switch (status) {
      case STATUS_OK: return '✅';
      case STATUS_ERROR: return '❌';
      case STATUS_SLOW: return '🐢';
      case STATUS_INFLIGHT: return '⏳';
      default: return '·';
    }
  };

  const statusColor = (status) => {
    switch (status) {
      case STATUS_OK: return '#4ade80';
      case STATUS_ERROR: return '#ef4444';
      case STATUS_SLOW: return '#fbbf24';
      case STATUS_INFLIGHT: return '#60a5fa';
      default: return '#94a3b8';
    }
  };

  const formatMs = (ms) => {
    if (ms == null) return '···';
    if (ms < 1000) return `${ms}ms`;
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    return new Date(timestamp).toISOString().substring(11, 23);
  };

  return (
    <div className="api-debug-panel">
      {/* Summary Bar */}
      <div className="api-debug-summary">
        <span className="api-debug-title">🔬 API Debug</span>
        <span className="api-debug-stat" style={{ color: '#94a3b8' }}>
          {stats.total} calls
        </span>
        {stats.errors > 0 && (
          <span className="api-debug-stat api-debug-stat-error" onClick={() => setFilter(f => f === 'errors' ? 'all' : 'errors')}>
            ❌ {stats.errors}
          </span>
        )}
        {stats.slow > 0 && (
          <span className="api-debug-stat api-debug-stat-slow" onClick={() => setFilter(f => f === 'slow' ? 'all' : 'slow')}>
            🐢 {stats.slow}
          </span>
        )}
        {stats.inflight > 0 && (
          <span className="api-debug-stat" style={{ color: '#60a5fa' }}>
            ⏳ {stats.inflight}
          </span>
        )}
        {filter !== 'all' && (
          <button className="api-debug-filter-badge" onClick={() => setFilter('all')}>
            {filter} ✕
          </button>
        )}
        <button className="api-debug-clear" onClick={() => tracker?.clear()}>clear</button>
      </div>

      {/* Analytics Debug (existing, integrated) */}
      <div className="api-debug-analytics">
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
          <span style={{ color: '#38bdf8', fontWeight: 600 }}>📊 Analytics</span>
          <span style={{ color: analyticsDebug.inflight ? '#fbbf24' : '#4ade80' }}>
            {analyticsDebug.inflight ? '⏳ inflight' : 'idle'}
          </span>
          {analyticsDebug.gatedCount > 0 && (
            <span style={{ color: '#fb923c' }}>gated:{analyticsDebug.gatedCount}</span>
          )}
          {pendingAnalytics.length > 0 && (
            <span style={{ color: '#fbbf24' }}>pending: {pendingAnalytics.join(', ')}</span>
          )}
        </div>
        {analyticsDebug.lastResult && (
          <div style={{ color: '#94a3b8', marginTop: '2px', fontSize: '9px' }}>{analyticsDebug.lastResult}</div>
        )}
        {moveStats && (
          <div style={{ color: '#6ee7b7', marginTop: '2px', fontSize: '9px' }}>
            quartiles: {moveStats.word_quartiles?.length > 0 ? moveStats.word_quartiles.join('·') : '(none)'} | vocab: {moveStats.word_rank}/{moveStats.word_count}
          </div>
        )}
      </div>

      {/* API Call Log */}
      <div className="api-debug-log">
        {filteredEntries.length === 0 && (
          <div className="api-debug-empty">No API calls recorded{filter !== 'all' ? ` (filter: ${filter})` : ''}</div>
        )}
        {filteredEntries.map(entry => (
          <div
            key={entry.id}
            className={`api-debug-entry api-debug-entry-${entry.status}`}
            onClick={() => toggleExpand(entry.id)}
          >
            {/* Compact row */}
            <div className="api-debug-entry-row">
              <span className="api-debug-entry-icon">{statusIcon(entry.status)}</span>
              <span className="api-debug-entry-method">{entry.method}</span>
              <span className="api-debug-entry-label">{entry.label}</span>
              <span className="api-debug-entry-duration" style={{ color: statusColor(entry.status) }}>
                {formatMs(entry.clientTiming.durationMs)}
              </span>
              {entry.httpStatus && entry.httpStatus !== 200 && (
                <span className="api-debug-entry-http">{entry.httpStatus}</span>
              )}
              <span className="api-debug-entry-time">{formatTime(entry.clientTiming.start)}</span>
            </div>

            {/* Error message inline */}
            {entry.error && (
              <div className="api-debug-entry-error">{entry.error}</div>
            )}

            {/* Expanded detail */}
            {expanded.has(entry.id) && (
              <div className="api-debug-entry-detail">
                <div className="api-debug-timing-section">
                  <div className="api-debug-timing-title">⏱ Client Timing</div>
                  <div className="api-debug-timing-row">
                    <span>Network</span>
                    <span>{entry.clientTiming.end ? formatMs(entry.clientTiming.end - entry.clientTiming.start) : '···'}</span>
                  </div>
                  {entry.clientTiming.parseEnd && (
                    <div className="api-debug-timing-row">
                      <span>Parse</span>
                      <span>{formatMs(entry.clientTiming.parseEnd - entry.clientTiming.end)}</span>
                    </div>
                  )}
                  <div className="api-debug-timing-row api-debug-timing-total">
                    <span>Total</span>
                    <span>{formatMs(entry.clientTiming.durationMs)}</span>
                  </div>
                </div>

                {/* Server-side timing */}
                {entry.serverTiming && (
                  <div className="api-debug-timing-section">
                    <div className="api-debug-timing-title">🖥 Server Timing</div>
                    {Object.entries(entry.serverTiming).map(([key, val]) => (
                      <div key={key} className="api-debug-timing-row">
                        <span>{key}</span>
                        <span>{typeof val === 'number' ? formatMs(val) : String(val)}</span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Extra metadata */}
                {entry.extra && Object.keys(entry.extra).length > 0 && (
                  <div className="api-debug-timing-section">
                    <div className="api-debug-timing-title">📎 Extra</div>
                    {Object.entries(entry.extra).map(([key, val]) => (
                      <div key={key} className="api-debug-timing-row">
                        <span>{key}</span>
                        <span>{typeof val === 'object' ? JSON.stringify(val) : String(val)}</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="api-debug-url">{entry.url}</div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* WebSocket Logs (existing, re-styled) */}
      {wsLogs.length > 0 && (
        <div className="api-debug-ws">
          <div className="api-debug-ws-title">📡 WebSocket</div>
          {wsLogs.map((log, i) => (
            <div key={i} className="api-debug-ws-entry">
              <span className="api-debug-ws-time">[{log.time}]</span> {log.msg}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ApiDebugPanel;
